import React from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { usePendingDisbursements, useInitiateDisbursement } from '../../hooks/disbursements/useDisbursements';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../hooks/auth/useAuth';

const Disbursements: React.FC = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { data: disbursements, isLoading, error } = usePendingDisbursements();
  const initiateMutation = useInitiateDisbursement();

  const handleInitiate = async (applicationId: string) => {
    try {
      await initiateMutation.mutateAsync({
        applicationId,
        disbursedBy: user?.userId || '',
      });
      enqueueSnackbar('Disbursement initiated successfully!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Disbursement failed',
        { variant: 'error' }
      );
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Disbursements
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load disbursements. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Disbursements
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage loan disbursements via M-Pesa
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Customer Name</strong></TableCell>
              <TableCell><strong>Approved Amount</strong></TableCell>
              <TableCell><strong>Disbursement Amount</strong></TableCell>
              <TableCell><strong>Tenor</strong></TableCell>
              <TableCell><strong>Purpose</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Applied Date</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : disbursements?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No pending disbursements</Typography>
                </TableCell>
              </TableRow>
            ) : (
              disbursements?.map((disbursement) => (
                <TableRow key={disbursement.applicationId} hover>
                  <TableCell>{disbursement.customerName}</TableCell>
                  <TableCell>{formatCurrency(parseFloat(disbursement.approvedAmount))}</TableCell>
                  <TableCell>
                    <strong style={{ color: '#2e7d32' }}>
                      {formatCurrency(parseFloat(disbursement.disbursementAmount))}
                    </strong>
                  </TableCell>
                  <TableCell>{disbursement.tenorWeeks} week(s)</TableCell>
                  <TableCell>{disbursement.purpose}</TableCell>
                  <TableCell>
                    <Chip
                      label={disbursement.applicationStatus}
                      size="small"
                      color={
                        disbursement.applicationStatus === 'APPROVED' ? 'success' :
                        disbursement.applicationStatus === 'PROCESSING' ? 'info' :
                        disbursement.applicationStatus === 'DISBURSED' ? 'primary' :
                        disbursement.applicationStatus === 'FAILED' ? 'error' :
                        'warning'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {disbursement.dateApplied && format(new Date(disbursement.dateApplied), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell align="right">
                    {disbursement.applicationStatus === 'APPROVED' && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SendIcon />}
                        onClick={() => handleInitiate(disbursement.applicationId)}
                        disabled={initiateMutation.isPending}
                      >
                        Disburse
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Disbursements;
